import signupImage from "@assets/images/smop-login.png";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  FormControl,
  FormHelperText,
  Grid,
  Input,
  InputLabel,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useSavedAccount } from "@saleor/components/AppLayout/SavedAccountContext";
import useNavigator from "@saleor/hooks/useNavigator";
import React from "react";
import { Link } from "react-router-dom";

import SignupController from "../../controllers/SignupController";
import {
  chooseAccountPath,
  passwordResetPath,
  userLoginPath
} from "./../../urls";

export interface SignupFormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}
export interface SignupCardProps {
  validations;
  onSubmit?(event: SignupFormData);
}
const useStyles = makeStyles(
  theme => ({
    alreadHaveAccount: {
      "& a": {
        color: "#1F4EF4"
      },
      marginTop: 30
    },
    err_fields: {
      backgroundColor: "#393636",
      color: "#fff",
      left: 0,
      padding: "10px 20px",
      position: "absolute",
      right: 0,
      top: "-40px",
      width: "100%"
    },
    forgotPassword: {
      backgroundColor: "#011671",
      bottom: 0,
      color: "#fff",
      left: 0,
      outline: "none",
      padding: "5px 20px",
      position: "absolute",
      right: 0,
      textDecoration: "none",
      width: "100%;"
    },
    formControl: {
      "& label": {
        zIndex: 9
      },
      margin: "-5px 0 0",
      width: "100%"
    },
    formHelper: {
      fontSize: "11px",
      margin: "3px 0 0",
      textAlign: "right"
    },
    formInput: {
      "& > input": {
        marginTop: "10px"
      },
      "& > input:-webkit-autofill": {
        borderBottom: "0.5px solid #0030FF",
        boxShadow: "inset 0 0 0px 9999px #0030FF6b"
      },
      fontSize: "14px",
      margin: "0 !important",
      paddingTop: "18px"
    },
    formSubmitBtn: {
      "& span": {
        color: "#fff"
      },
      backgroundColor: "#FFD15C",
      borderRadius: 0,
      color: "#fff",
      fontSize: "12px",
      marginLeft: "10px",
      padding: "2px 40px"
    },
    loginHere: {
      "&:hover": {
        cursor: "pointer"
      },
      color: "#1F4EF4",
      fontSize: "0.875rem",
      textDecoration: "underline"
    },
    mainCard: {
      alignItems: "center",
      display: "flex",
      justifyContent: "center",
      paddingBottom: "50px",
      position: "relative"
    },
    root: {
      alignItems: "center",
      display: "flex",
      height: "100vH",
      justifyContent: "center",
      maxWidth: 800,
      padding: theme.spacing(6)
    },
    signupForm: {
      display: "flex"
    },
    signupImage: {
      position: "relative",
      top: 100,
      width: "100%"
    }
  }),
  { name: "SignupPage" }
);

const SignupCard: React.FC<SignupCardProps> = props => {
  const { validations, onSubmit } = props;
  const { user, handleChange } = SignupController();
  const classes = useStyles(props);
  const { err, err_msg } = validations;
  const { savedAccounts } = useSavedAccount();
  const navigate = useNavigator();

  const handleLoginNavigation = () => {
    if (savedAccounts.length) {
      navigate(chooseAccountPath);
    } else {
      navigate(userLoginPath);
    }
  };

  return (
    <Container className={classes.root}>
      <Card className={classes.mainCard}>
        <CardContent>
          {err_msg.length ? (
            <Box className={classes.err_fields}>
              {err_msg.length
                ? err_msg.map(err => err.message)
                : "Invalid Form! Please complete the fields"}
            </Box>
          ) : (
            ""
          )}
          <form
            method="POST"
            onSubmit={e => {
              e.preventDefault();
              onSubmit(user);
            }}
            noValidate
            autoComplete="off"
          >
            <Grid container>
              <Grid item xs={5}>
                <img className={classes.signupImage} src={signupImage} />
              </Grid>
              <Grid item xs={7}>
                <Grid container>
                  <Grid item xs={12}>
                    <strong>Create your Online Seller Account!</strong>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl
                      error={err.firstName ? true : false}
                      className={classes.formControl}
                      onChange={handleChange}
                    >
                      <InputLabel htmlFor="firstName">Firstname: *</InputLabel>
                      <Input
                        className={classes.formInput}
                        name="firstName"
                        id="firstName"
                        value={user.firstName || ""}
                      />
                      <FormHelperText className={classes.formHelper}>
                        {err.firstName ? err.firstName : ""}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl
                      error={err.lastName ? true : false}
                      className={classes.formControl}
                      onChange={handleChange}
                    >
                      <InputLabel htmlFor="lastName">Lastname: *</InputLabel>
                      <Input
                        className={classes.formInput}
                        name="lastName"
                        id="lastName"
                        value={user.lastName || ""}
                      />
                      <FormHelperText className={classes.formHelper}>
                        {err.lastName ? err.lastName : ""}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl
                      error={err.email ? true : false}
                      className={classes.formControl}
                      onChange={handleChange}
                    >
                      <InputLabel htmlFor="email">Email Address: *</InputLabel>
                      <Input
                        className={classes.formInput}
                        name="email"
                        id="email"
                        value={user.email || ""}
                      />
                      <FormHelperText className={classes.formHelper}>
                        {err.email ? err.email : ""}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl
                      error={err.password ? true : false}
                      className={classes.formControl}
                      onChange={handleChange}
                    >
                      <InputLabel htmlFor="password">Password: *</InputLabel>
                      <Input
                        className={classes.formInput}
                        name="password"
                        id="password"
                        type="password"
                        value={user.password || ""}
                      />
                      <FormHelperText className={classes.formHelper}>
                        {err.password ? err.password : ""}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl
                      error={err.confirm_password ? true : false}
                      className={classes.formControl}
                      onChange={handleChange}
                    >
                      <InputLabel htmlFor="confirm_password">
                        Confirm Password: *
                      </InputLabel>
                      <Input
                        className={classes.formInput}
                        name="confirm_password"
                        id="confirm_password"
                        type="password"
                        value={user.confirm_password || ""}
                      />
                      <FormHelperText className={classes.formHelper}>
                        {err.confirm_password ? err.confirm_password : ""}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <Box className={classes.alreadHaveAccount}>
                      Already have account?{" "}
                      <Typography
                        className={classes.loginHere}
                        component="a"
                        onClick={handleLoginNavigation}
                      >
                        Login Here
                      </Typography>
                      <Button
                        type="submit"
                        className={classes.formSubmitBtn}
                        variant="contained"
                        color="primary"
                      >
                        Next
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </form>
          <Link className={classes.forgotPassword} to={passwordResetPath}>
            Forgot your Password?
          </Link>
        </CardContent>
      </Card>
    </Container>
  );
};
SignupCard.displayName = "SignupCard";
export default SignupCard;
